import PageHeader from '../../components/PageHeader/PageHeader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Form, Modal, Spinner } from 'react-bootstrap';
import {
  LuBellRing,
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuCircleDollarSign,
  LuDownload,
  LuFilter,
  LuMailPlus,
  LuPlus,
  LuRefreshCw,
  LuSearch,
  LuX,
} from 'react-icons/lu';
import { notificar } from '../../components/SistemaToast/notificar';
import DateInput from '../../components/DateInput/DateInput';
import { urlApiProd } from '../utiles';
import { exportControlExcel, generarCorreoOutlookConExcel } from './finanzasProveedoresExcel';
import './FinanzasProveedores.css';

const EMPTY_DATA = {
  estados_pago: [],
  proveedores: [],
  resumen_global: {},
  resumen_filtrado: {},
  resumen_proveedores: [],
  notas_fiscales: [],
  alertas: [],
  descuentos: [],
  solicitudes: [],
};

const EXTRA_FILTERS = {
  nro_nf: { label: 'N° NF', type: 'text' },
  mes: { label: 'Mes emisión', type: 'month' },
  anio: { label: 'Año emisión', type: 'number' },
  almacen: { label: 'Almacén', type: 'text' },
  tipo_movimiento: { label: 'Tipo movimiento', type: 'text' },
  producto: { label: 'Producto', type: 'text' },
  fecha_desde: { label: 'Fecha NF desde', type: 'date' },
  fecha_hasta: { label: 'Fecha NF hasta', type: 'date' },
};

const DISCOUNT_TYPES = ['Des-log', 'Des-peso', 'Des-claim', 'Des-piezas'];
const PAGE_SIZE = 25;
const PROVIDER_PAGE_SIZE = 10;
const DEFAULT_ACCOUNT_FILTERS = ['fecha_desde', 'fecha_hasta'];

function currentYearRange() {
  const year = new Date().getFullYear();
  return { desde: `${year}-01-01`, hasta: `${year}-12-31` };
}

const num = (value) => Number(value) || 0;
const fmtM3 = (value) => new Intl.NumberFormat('es-CL', { maximumFractionDigits: 3 }).format(num(value));
const fmtBRL = (value) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'BRL' }).format(num(value));
const fmtDate = (value) => value ? new Intl.DateTimeFormat('es-CL').format(new Date(`${String(value).slice(0, 10)}T12:00:00`)) : '-';
const paymentStatusLabel = (status) => status === 'Vencida' ? 'Vencido' : status;

function nextDiscountCorrelativo(descuentos) {
  const year = new Date().getUTCFullYear();
  const maxId = (descuentos || []).reduce((max, d) => Math.max(max, Number(d.id) || 0), 0);
  return `DES-${year}-${String(maxId + 1).padStart(5, '0')}`;
}

function isDiscountEditable(discount) {
  return discount?.es_borrador || discount?.estado === 'Pendiente de Descontar';
}

function discountStatusLabel(discount) {
  if (!discount) return '';
  return discount.es_borrador ? 'Borrador' : discount.estado;
}

function discountStatusClass(discount) {
  if (!discount) return 'pending';
  if (discount.es_borrador) return 'review';
  if (discount.estado === 'Descontado') return 'paid';
  return 'pending';
}

function validateDiscountForm(form, esBorrador, distributed) {
  if (!form.id_proveedor_responsable) {
    return 'Seleccione el proveedor responsable del descuento.';
  }
  if (!esBorrador) {
    if (!form.tipo) return 'Seleccione el tipo de descuento.';
    if (!form.fecha_origen) return 'Indique la fecha de origen.';
    const valor = num(form.valor);
    if (!valor || valor <= 0) return 'Indique el valor del descuento.';
    if (Math.abs(distributed - valor) >= 0.005) {
      return 'La distribución entre NF debe coincidir con el valor total del descuento.';
    }
  }
  return null;
}

function discountToForm(discount, today) {
  if (!discount) {
    return { moneda: 'BRL', fecha_origen: today, fecha_aplicacion: today, nf_distribuciones: [] };
  }
  return {
    tipo: discount.tipo || '',
    id_proveedor_responsable: discount.id_proveedor_responsable || '',
    fecha_origen: discount.fecha_origen ? String(discount.fecha_origen).slice(0, 10) : today,
    fecha_aplicacion: discount.fecha_aplicacion ? String(discount.fecha_aplicacion).slice(0, 10) : today,
    proveedor_extracosto: discount.proveedor_extracosto || '',
    nro_invoice: discount.nro_invoice || '',
    nro_bl: discount.nro_bl || '',
    nro_booking: discount.nro_booking || '',
    m3: discount.m3 ?? '',
    piezas: discount.piezas ?? '',
    valor: discount.valor ?? '',
    moneda: discount.moneda || 'BRL',
    observaciones: discount.observaciones || '',
    nf_distribuciones: (discount.nf_distribuciones || []).map((row) => ({
      id_nf: row.id_nf,
      monto: row.monto,
    })),
  };
}

function statusClass(status) {
  if (status === 'Pagado') return 'paid';
  if (status === 'Vencida' || status === 'Vencido') return 'overdue';
  if (status === 'Solicitud de pago generada') return 'requested';
  if (status === 'En revisión') return 'review';
  return 'pending';
}

function SummaryTile({ label, value, money = false, accent }) {
  return (
    <div className={`fin-summary-tile ${accent || ''}`}>
      <span>{label}</span>
      <strong>{money ? fmtBRL(value) : fmtM3(value)}</strong>
    </div>
  );
}

function SummaryGrid({ data }) {
  return (
    <div className="fin-summary-grid">
      <SummaryTile label="Total M3 comprado" value={data.total_m3_comprado} />
      <SummaryTile label="Total R$ pagado" value={data.total_brl_pagado} money accent="positive" />
      <SummaryTile label="M3 pendiente de pago" value={data.total_m3_pendiente} accent="attention" />
      <SummaryTile label="R$ pendiente de pago" value={data.total_brl_pendiente} money accent="attention" />
      <SummaryTile label="Extracostos descontados" value={data.total_extracostos_descontados} money />
      <SummaryTile label="Extracostos por descontar" value={data.total_extracostos_por_descontar} money accent="attention" />
    </div>
  );
}

function RefreshIconButton({ title, onClick }) {
  const [animationKey, setAnimationKey] = useState(0);
  return (
    <button
      type="button"
      className="fin-icon-button"
      title={title}
      aria-label={title}
      onClick={() => {
        setAnimationKey((key) => key + 1);
        onClick();
      }}
    >
      <LuRefreshCw
        key={animationKey}
        className={animationKey ? 'fin-refresh-spin' : ''}
      />
    </button>
  );
}

function Filters({ filters, setFilters, activeExtras, setActiveExtras, proveedores, estados }) {
  const [providerQuery, setProviderQuery] = useState('');
  const [providerListOpen, setProviderListOpen] = useState(false);
  const providerSearchRef = useRef(null);
  const normalizedProviderQuery = providerQuery.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const matchingProviders = useMemo(() => proveedores.filter((provider) => (
    provider.nombre.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(normalizedProviderQuery)
  )), [normalizedProviderQuery, proveedores]);

  useEffect(() => {
    const selectedProvider = proveedores.find((provider) => String(provider.id_proveedor) === String(filters.proveedor));
    if (selectedProvider) setProviderQuery(selectedProvider.nombre);
  }, [filters.proveedor, proveedores]);

  useEffect(() => {
    if (!providerListOpen) return undefined;
    const closeProviderList = (event) => {
      if (!providerSearchRef.current?.contains(event.target)) setProviderListOpen(false);
    };
    document.addEventListener('mousedown', closeProviderList);
    return () => document.removeEventListener('mousedown', closeProviderList);
  }, [providerListOpen]);

  const selectProvider = (provider) => {
    setProviderQuery(provider?.nombre || '');
    setProviderListOpen(false);
    setFilters((prev) => ({ ...prev, proveedor: provider ? String(provider.id_proveedor) : '' }));
  };

  const addExtra = (event) => {
    const key = event.target.value;
    if (key && !activeExtras.includes(key)) setActiveExtras((prev) => [...prev, key]);
    event.target.value = '';
  };
  const removeExtra = (key) => {
    setActiveExtras((prev) => prev.filter((item) => item !== key));
    setFilters((prev) => ({ ...prev, [key]: '' }));
  };
  return (
    <div className="fin-filterbar">
      <label className="fin-provider-search">
        <span>Proveedor</span>
        <div className="fin-provider-search-control" ref={providerSearchRef}>
          <input
            type="search"
            value={providerQuery}
            placeholder="Buscar proveedor..."
            autoComplete="off"
            role="combobox"
            aria-expanded={providerListOpen}
            aria-controls="fin-provider-options"
            onFocus={(event) => { event.currentTarget.select(); setProviderListOpen(true); }}
            onChange={(event) => {
              setProviderQuery(event.target.value);
              setProviderListOpen(true);
              if (filters.proveedor) setFilters((prev) => ({ ...prev, proveedor: '' }));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setProviderListOpen(false);
              if (event.key === 'Enter' && providerListOpen && matchingProviders.length) {
                event.preventDefault();
                selectProvider(matchingProviders[0]);
              }
            }}
          />
          {providerListOpen && (
            <div className="fin-provider-options" id="fin-provider-options" role="listbox">
              {!normalizedProviderQuery && <button type="button" role="option" aria-selected={!filters.proveedor} onMouseDown={(event) => event.preventDefault()} onClick={() => selectProvider(null)}>Todos los proveedores</button>}
              {matchingProviders.map((provider) => (
                <button key={provider.id_proveedor} type="button" role="option" aria-selected={String(filters.proveedor) === String(provider.id_proveedor)} onMouseDown={(event) => event.preventDefault()} onClick={() => selectProvider(provider)}>{provider.nombre}</button>
              ))}
              {!matchingProviders.length && <p>No se encontraron proveedores.</p>}
            </div>
          )}
        </div>
      </label>
      <label>
        <span>Estado de pago</span>
        <select value={filters.estado} onChange={(e) => setFilters((p) => ({ ...p, estado: e.target.value }))}>
          <option value="">Todos los estados</option>
          {estados.map((estado) => <option key={estado} value={estado}>{paymentStatusLabel(estado)}</option>)}
        </select>
      </label>
      {activeExtras.map((key) => {
        const config = EXTRA_FILTERS[key];
        if (!config) return null;
        const isDefaultFilter = DEFAULT_ACCOUNT_FILTERS.includes(key);
        if (config.type === 'month') {
          return (
            <label key={key}>
              <span>{config.label} <button type="button" className="fin-filter-remove" onClick={() => removeExtra(key)}>×</button></span>
              <select value={filters[key] || ''} onChange={(e) => setFilters((p) => ({ ...p, [key]: e.target.value }))}>
                <option value="">Todos</option>
                {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{new Intl.DateTimeFormat('es-CL', { month: 'long' }).format(new Date(2024, i, 1))}</option>)}
              </select>
            </label>
          );
        }
        if (config.type === 'date') {
          return (
            <label key={key}>
              <span>{config.label} {!isDefaultFilter && <button type="button" className="fin-filter-remove" onClick={() => removeExtra(key)}>×</button>}</span>
              <DateInput
                plain
                value={filters[key] || ''}
                min={key === 'fecha_hasta' ? filters.fecha_desde || undefined : undefined}
                max={key === 'fecha_desde' ? filters.fecha_hasta || undefined : undefined}
                onChange={(e) => setFilters((p) => ({ ...p, [key]: e.target.value }))}
              />
            </label>
          );
        }
        return (
          <label key={key}>
            <span>{config.label} {!isDefaultFilter && <button type="button" className="fin-filter-remove" onClick={() => removeExtra(key)}>×</button>}</span>
            <input
              type={config.type}
              value={filters[key] || ''}
              onChange={(e) => setFilters((p) => ({ ...p, [key]: e.target.value }))}
            />
          </label>
        );
      })}
      <label className="fin-add-filter">
        <span><LuFilter /> Filtros</span>
        <select defaultValue="" onChange={addExtra}>
          <option value="">Agregar filtro...</option>
          {Object.entries(EXTRA_FILTERS).filter(([key]) => !activeExtras.includes(key) && !DEFAULT_ACCOUNT_FILTERS.includes(key)).map(([key, item]) => (
            <option key={key} value={key}>{item.label}</option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="fin-button secondary fin-clear-filters"
        onClick={() => {
          const yearRange = currentYearRange();
          setProviderQuery('');
          setProviderListOpen(false);
          setFilters({ proveedor: '', estado: '', fecha_desde: yearRange.desde, fecha_hasta: yearRange.hasta });
          setActiveExtras([...DEFAULT_ACCOUNT_FILTERS]);
        }}
      >
        <LuX /> Limpiar
      </button>
    </div>
  );
}

function ProviderSummaryTable({ rows, onSelect }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PROVIDER_PAGE_SIZE));
  const pagedRows = rows.slice((page - 1) * PROVIDER_PAGE_SIZE, page * PROVIDER_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [rows]);

  return (
    <>
      <div className="fin-table-wrap">
        <table className="fin-table fin-provider-table">
          <thead><tr><th>Proveedor</th><th>Total M3 comprado</th><th>Total R$ pagado</th><th>M3 pendiente</th><th>R$ pendiente</th><th>Extracostos descontados</th><th>Extracostos por descontar</th></tr></thead>
          <tbody>
            {pagedRows.map((row) => (
              <tr key={row.id_proveedor} tabIndex="0" onClick={() => onSelect(row.id_proveedor)} onKeyDown={(e) => e.key === 'Enter' && onSelect(row.id_proveedor)}>
                <td><strong>{row.proveedor}</strong></td><td>{fmtM3(row.total_m3_comprado)}</td><td>{fmtBRL(row.total_brl_pagado)}</td><td>{fmtM3(row.total_m3_pendiente)}</td><td>{fmtBRL(row.total_brl_pendiente)}</td><td>{fmtBRL(row.total_extracostos_descontados)}</td><td>{fmtBRL(row.total_extracostos_por_descontar)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="7" className="fin-empty">No hay proveedores para los filtros aplicados.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="fin-pagination">
        <span>{rows.length ? `${(page - 1) * PROVIDER_PAGE_SIZE + 1}-${Math.min(page * PROVIDER_PAGE_SIZE, rows.length)} de ${rows.length} proveedores` : '0 proveedores'}</span>
        <button type="button" title="Página anterior" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><LuChevronLeft /></button>
        <strong>{page} / {totalPages}</strong>
        <button type="button" title="Página siguiente" disabled={page === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}><LuChevronRight /></button>
      </div>
    </>
  );
}

function compactValues(nf, field) {
  return [...new Set((nf.lineas || []).map((ln) => ln[field]).filter(Boolean))].join(' / ') || '-';
}

function AccountTable({ rows, selected, setSelected, selectedSupplierId, onProviderNeeded }) {
  const selectableRows = rows.filter((nf) => nf.estado_pago !== 'Pagado');
  const allVisibleSelected = selectableRows.length > 0 && selectableRows.every((nf) => selected.has(nf.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allVisibleSelected) selectableRows.forEach((nf) => next.delete(nf.id));
    else {
      const supplierIds = new Set(selectableRows.map((nf) => nf.id_proveedor));
      if (supplierIds.size > 1 || (selectedSupplierId && !supplierIds.has(selectedSupplierId))) return onProviderNeeded();
      selectableRows.forEach((nf) => next.add(nf.id));
    }
    setSelected(next);
  };
  const toggle = (nf) => {
    const next = new Set(selected);
    if (next.has(nf.id)) next.delete(nf.id);
    else {
      if (selectedSupplierId && selectedSupplierId !== nf.id_proveedor) return onProviderNeeded();
      next.add(nf.id);
    }
    setSelected(next);
  };
  return (
    <div className="fin-table-wrap fin-account-wrap">
      <table className="fin-table fin-account-table">
        <thead><tr><th className="fin-check-cell"><input aria-label="Seleccionar visibles" type="checkbox" disabled={!selectableRows.length} checked={allVisibleSelected} onChange={toggleAll} /></th><th>N° NF</th><th>Mes</th><th>Año</th><th>Espesor</th><th>Ancho</th><th>Largo</th><th>Almacén</th><th>Tipo movimiento</th><th>Producto</th><th>M3</th><th>Total NF (R$)</th><th>Estado de pago</th><th>Fecha vencimiento</th><th>Días vencidos</th></tr></thead>
        <tbody>
          {rows.map((nf) => {
            const blocked = nf.estado_pago === 'Pagado';
            return (
              <tr key={nf.id} className={selected.has(nf.id) ? 'selected' : ''}>
                <td className="fin-check-cell"><input aria-label={`Seleccionar NF ${nf.nro_nf}`} type="checkbox" disabled={blocked} checked={selected.has(nf.id)} onChange={() => toggle(nf)} /></td>
                <td><strong>{nf.nro_nf}</strong></td><td>{nf.mes_emision || '-'}</td><td>{nf.anio_emision || '-'}</td><td>{compactValues(nf, 'espesor')}</td><td>{compactValues(nf, 'ancho')}</td><td>{compactValues(nf, 'largo')}</td><td>{nf.almacen || '-'}</td><td>{nf.tipo_movimiento || '-'}</td><td>{nf.producto || '-'}</td><td>{fmtM3(nf.m3)}</td><td>{fmtBRL(nf.total_nf_brl)}</td><td><span className={`fin-status ${statusClass(nf.estado_pago)}`}>{paymentStatusLabel(nf.estado_pago)}</span></td><td>{fmtDate(nf.fecha_vencimiento)}</td><td className={nf.dias_vencidos ? 'fin-overdue-days' : ''}>{nf.dias_vencidos ?? '-'}</td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan="15" className="fin-empty">No hay Notas Fiscales para mostrar.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function DiscountModal({ show, onHide, proveedores, notas, descuentos, discount, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const isEditing = Boolean(discount?.id);
  const [form, setForm] = useState(() => discountToForm(discount, today));
  const [saving, setSaving] = useState(false);
  const [providerNfs, setProviderNfs] = useState([]);
  const nextCorrelativo = useMemo(() => nextDiscountCorrelativo(descuentos), [descuentos]);
  const correlativoLabel = isEditing ? discount.correlativo : nextCorrelativo;
  const distributed = form.nf_distribuciones.reduce((sum, row) => sum + num(row.monto), 0);
  const discountValue = num(form.valor);
  const distributionExceeds = discountValue > 0 && distributed > discountValue + 0.005;
  const distributionBalanced = discountValue > 0 && Math.abs(distributed - discountValue) < 0.005;
  useEffect(() => {
    if (!show) return;
    setForm(discountToForm(discount, today));
  }, [show, discount, today]);
  useEffect(() => {
    const providerId = form.id_proveedor_responsable;
    if (!show || !providerId) { setProviderNfs([]); return undefined; }
    let active = true;
    fetch(`${urlApiProd}/finanzas/proveedores?proveedor=${providerId}`)
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => { if (active && ok) setProviderNfs(body.notas_fiscales || []); })
      .catch(() => { if (active) setProviderNfs(notas.filter((nf) => String(nf.id_proveedor) === String(providerId))); });
    return () => { active = false; };
  }, [form.id_proveedor_responsable, notas, show]);
  const patch = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const patchDistribution = (nfId, value) => setForm((prev) => {
    const rest = prev.nf_distribuciones.filter((row) => row.id_nf !== nfId);
    return { ...prev, nf_distribuciones: value === '' ? rest : [...rest, { id_nf: nfId, monto: value }] };
  });
  const save = async (esBorrador) => {
    if (distributionExceeds) {
      notificar.aviso(`La suma distribuida (${fmtBRL(distributed)}) supera el valor del descuento (${fmtBRL(discountValue)}). Corrija los montos antes de guardar.`);
      return;
    }
    const validationError = validateDiscountForm(form, esBorrador, distributed);
    if (validationError) {
      notificar.aviso(validationError);
      return;
    }
    setSaving(true);
    try {
      const url = isEditing
        ? `${urlApiProd}/finanzas/proveedores/descuentos/${discount.id}`
        : `${urlApiProd}/finanzas/proveedores/descuentos`;
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, es_borrador: esBorrador }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'No fue posible guardar el descuento.');
      notificar.exito(esBorrador ? 'Borrador guardado.' : isEditing ? 'Descuento actualizado.' : 'Descuento disponible para aplicar en una solicitud.');
      onSaved(); onHide();
    } catch (error) {
      notificar.error(error.message === 'Failed to fetch'
        ? 'No fue posible guardar el descuento. Verifique los datos e intente nuevamente.'
        : error.message);
    } finally { setSaving(false); }
  };
  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <div className="fin-discount-modal-header">
          <div className="fin-discount-modal-heading">
            <Modal.Title className="fin-discount-modal-title">
              {isEditing ? 'Editar descuento' : 'Nuevo descuento'}
            </Modal.Title>
            {isEditing ? (
              <span className={`fin-status ${discountStatusClass(discount)}`}>{discountStatusLabel(discount)}</span>
            ) : null}
          </div>
          <span className="fin-discount-modal-correlativo">N° {correlativoLabel}</span>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="fin-form-grid">
          <Form.Group><Form.Label>Tipo de descuento</Form.Label><Form.Select value={form.tipo || ''} onChange={(e) => patch('tipo', e.target.value)}><option value="">Seleccione</option>{DISCOUNT_TYPES.map((x) => <option key={x}>{x}</option>)}</Form.Select></Form.Group>
          <Form.Group><Form.Label>Proveedor de material responsable</Form.Label><Form.Select value={form.id_proveedor_responsable || ''} onChange={(e) => setForm((p) => ({ ...p, id_proveedor_responsable: e.target.value, nf_distribuciones: [] }))}><option value="">Seleccione</option>{proveedores.map((p) => <option key={p.id_proveedor} value={p.id_proveedor}>{p.nombre}</option>)}</Form.Select></Form.Group>
          <Form.Group><Form.Label>Fecha de origen</Form.Label><DateInput value={form.fecha_origen || ''} onChange={(e) => patch('fecha_origen', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>Proveedor asociado al extracosto</Form.Label><Form.Control value={form.proveedor_extracosto || ''} onChange={(e) => patch('proveedor_extracosto', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>Fecha realización / aplicación</Form.Label><DateInput value={form.fecha_aplicacion || ''} onChange={(e) => patch('fecha_aplicacion', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>N° Invoice</Form.Label><Form.Control value={form.nro_invoice || ''} onChange={(e) => patch('nro_invoice', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>N° BL</Form.Label><Form.Control value={form.nro_bl || ''} onChange={(e) => patch('nro_bl', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>N° Booking</Form.Label><Form.Control value={form.nro_booking || ''} onChange={(e) => patch('nro_booking', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>M3 asociados</Form.Label><Form.Control type="number" step="0.001" value={form.m3 || ''} onChange={(e) => patch('m3', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>Cantidad de piezas</Form.Label><Form.Control type="number" value={form.piezas || ''} onChange={(e) => patch('piezas', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>Valor del descuento</Form.Label><Form.Control type="number" step="0.01" value={form.valor || ''} onChange={(e) => patch('valor', e.target.value)} /></Form.Group>
          <Form.Group><Form.Label>Moneda</Form.Label><Form.Select value={form.moneda || 'BRL'} onChange={(e) => patch('moneda', e.target.value)}><option value="BRL">BRL (R$)</option><option value="USD">USD</option></Form.Select></Form.Group>
          <Form.Group className="fin-form-wide"><Form.Label>Observaciones</Form.Label><Form.Control as="textarea" rows={2} value={form.observaciones || ''} onChange={(e) => patch('observaciones', e.target.value)} /></Form.Group>
        </div>
        <h3 className="fin-modal-heading">Distribución entre Notas Fiscales</h3>
        <div className="fin-table-wrap fin-modal-table"><table className="fin-table"><thead><tr><th>N° NF</th><th>Fecha NF</th><th>OC</th><th>Grado</th><th>Dimensión</th><th>Paquetes</th><th>Pcs/Paq</th><th>Total piezas</th><th>M3</th><th>Monto NF</th><th>Monto descuento</th></tr></thead><tbody>
          {providerNfs.map((nf) => <tr key={nf.id}><td>{nf.nro_nf}</td><td>{fmtDate(nf.fecha_nf)}</td><td>{nf.oc || '-'}</td><td>{compactValues(nf, 'grado')}</td><td>{[compactValues(nf, 'espesor'), compactValues(nf, 'ancho'), compactValues(nf, 'largo')].join(' x ')}</td><td>{(nf.lineas || []).reduce((s, x) => s + num(x.paquetes), 0)}</td><td>{compactValues(nf, 'pcs_paq')}</td><td>{(nf.lineas || []).reduce((s, x) => s + num(x.total_pcs), 0)}</td><td>{fmtM3(nf.m3)}</td><td>{fmtBRL(nf.total_nf_brl)}</td><td><input className="fin-distribution-input" type="number" min="0" step="0.01" value={form.nf_distribuciones.find((x) => x.id_nf === nf.id)?.monto || ''} onChange={(e) => patchDistribution(nf.id, e.target.value)} /></td></tr>)}
          {!providerNfs.length && <tr><td colSpan="11" className="fin-empty">Seleccione un proveedor para distribuir el descuento.</td></tr>}
        </tbody></table></div>
        <div className={`fin-distribution-total ${distributionBalanced ? 'balanced' : distributionExceeds ? 'exceeded' : ''}`}><span>Distribuido</span><strong>{fmtBRL(distributed)} / {fmtBRL(form.valor)}</strong>{distributionExceeds ? <span className="fin-distribution-error">La distribución supera el valor del descuento.</span> : null}</div>
      </Modal.Body>
      <Modal.Footer><button className="fin-button secondary" type="button" onClick={onHide}>Cancelar</button><button className="fin-button secondary" disabled={saving || distributionExceeds} type="button" onClick={() => save(true)}>Guardar borrador</button><button className="fin-button primary" disabled={saving || distributionExceeds} type="button" onClick={() => save(false)}>{saving ? <Spinner size="sm" /> : <LuCheck />} Guardar y aplicar</button></Modal.Footer>
    </Modal>
  );
}

function PaymentModal({ show, onHide, selectedNfs, discounts, onGenerated }) {
  const [motivo, setMotivo] = useState('');
  const [selectedDiscounts, setSelectedDiscounts] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const providerId = selectedNfs[0]?.id_proveedor;
  const pending = discounts.filter((d) => d.id_proveedor_responsable === providerId && !d.es_borrador && d.estado === 'Pendiente de Descontar');
  const gross = selectedNfs.reduce((s, nf) => s + num(nf.total_nf_brl), 0);
  const discountTotal = pending.filter((d) => selectedDiscounts.has(d.id)).reduce((s, d) => s + num(d.valor), 0);
  useEffect(() => { if (!show) { setMotivo(''); setSelectedDiscounts(new Set()); } }, [show]);
  const generate = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${urlApiProd}/finanzas/proveedores/solicitudes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nf_ids: selectedNfs.map((nf) => nf.id), descuento_ids: [...selectedDiscounts], motivo }) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'No fue posible generar la solicitud.');
      onGenerated(body.solicitud); onHide();
    } catch (error) { notificar.error(error.message); } finally { setSaving(false); }
  };
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton><Modal.Title>Generar solicitud de pago</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="fin-request-summary"><span>{selectedNfs[0]?.proveedor}</span><strong>{selectedNfs.length} NF · {fmtBRL(gross)}</strong></div>
        <Form.Group className="mb-3"><Form.Label>Condición / motivo</Form.Label><Form.Control value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={selectedNfs[0]?.condicion_pago || 'Motivo de la transferencia'} /></Form.Group>
        {pending.length ? <div className="fin-discount-warning"><div><LuBellRing /><strong>Existen descuentos pendientes para este proveedor</strong></div>{pending.map((d) => <label key={d.id}><input type="checkbox" disabled={d.moneda !== 'BRL'} checked={selectedDiscounts.has(d.id)} onChange={() => setSelectedDiscounts((prev) => { const next = new Set(prev); next.has(d.id) ? next.delete(d.id) : next.add(d.id); return next; })} /><span>{d.correlativo} · {d.tipo} · {d.moneda} {num(d.valor).toLocaleString('es-CL')} {d.moneda !== 'BRL' ? '(requiere conversión antes de aplicar)' : ''}</span></label>)}</div> : null}
        <div className="fin-net-total"><span>Total NF</span><strong>{fmtBRL(gross)}</strong><span>Descuentos</span><strong>- {fmtBRL(discountTotal)}</strong><span>Valor neto solicitado</span><strong>{fmtBRL(gross - discountTotal)}</strong></div>
        <p className="fin-outlook-note">Se descargará un borrador de Outlook (`.eml`). Ábralo: queda listo para editar, adjuntar los PDF de las NF y enviar.</p>
      </Modal.Body>
      <Modal.Footer><button className="fin-button secondary" type="button" onClick={onHide}>Cancelar</button><button className="fin-button primary" disabled={saving || discountTotal > gross} type="button" onClick={generate}>{saving ? <Spinner size="sm" /> : <LuMailPlus />} Generar solicitud</button></Modal.Footer>
    </Modal>
  );
}

export default function FinanzasProveedores() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(EMPTY_DATA);
  const [filters, setFilters] = useState(() => {
    const yearRange = currentYearRange();
    return {
      proveedor: searchParams.get('proveedor') || '',
      estado: searchParams.get('estado') || '',
      fecha_desde: searchParams.get('fecha_desde') || yearRange.desde,
      fecha_hasta: searchParams.get('fecha_hasta') || yearRange.hasta,
    };
  });
  const [activeExtras, setActiveExtras] = useState(DEFAULT_ACCOUNT_FILTERS);
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('cuenta');
  const [discountModal, setDiscountModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [paymentModal, setPaymentModal] = useState(false);
  const [discountFilters, setDiscountFilters] = useState({ search: '', tipo: '', estado: '', fecha: '' });
  const [globalRange, setGlobalRange] = useState(currentYearRange);

  useEffect(() => {
    const proveedor = searchParams.get('proveedor') || '';
    const estado = searchParams.get('estado') || '';
    setFilters((prev) => {
      if (prev.proveedor === proveedor && prev.estado === estado) return prev;
      return { ...prev, proveedor, estado };
    });
  }, [searchParams]);

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => value !== '' && value != null && params.set(key, value));
      if (globalRange.desde) params.set('global_fecha_desde', globalRange.desde);
      if (globalRange.hasta) params.set('global_fecha_hasta', globalRange.hasta);
      const response = await fetch(`${urlApiProd}/finanzas/proveedores?${params.toString()}`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'No fue posible cargar el control de proveedores.');
      setData({ ...EMPTY_DATA, ...body });
    } catch (error) { notificar.error(error.message); }
  }, [filters, globalRange]);

  useEffect(() => {
    const timer = setTimeout(loadData, 250);
    return () => clearTimeout(timer);
  }, [loadData]);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [filters]);

  const selectedNfs = useMemo(() => data.notas_fiscales.filter((nf) => selected.has(nf.id)), [data.notas_fiscales, selected]);
  const canGeneratePaymentRequest = selectedNfs.length > 0 && selectedNfs.every((nf) => nf.estado_pago !== 'Solicitud de pago generada' && nf.estado_pago !== 'Pagado');
  const selectedProviderId = filters.proveedor || selectedNfs[0]?.id_proveedor || '';
  const accountRows = useMemo(() => selectedProviderId ? data.notas_fiscales.filter((nf) => String(nf.id_proveedor) === String(selectedProviderId)) : data.notas_fiscales, [data.notas_fiscales, selectedProviderId]);
  const totalPages = Math.max(1, Math.ceil(accountRows.length / PAGE_SIZE));
  const pagedRows = accountRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const providerSummary = data.resumen_proveedores.find((row) => String(row.id_proveedor) === String(selectedProviderId)) || data.resumen_filtrado;
  const filteredDiscounts = data.descuentos.filter((d) => {
    if (selectedProviderId && String(d.id_proveedor_responsable) !== String(selectedProviderId)) return false;
    if (discountFilters.tipo && d.tipo !== discountFilters.tipo) return false;
    const estado = d.es_borrador ? 'Borrador' : d.estado;
    if (discountFilters.estado && estado !== discountFilters.estado) return false;
    if (discountFilters.fecha && String(d.fecha_origen || '').slice(0, 10) !== discountFilters.fecha) return false;
    const search = discountFilters.search.trim().toLowerCase();
    if (search && ![d.correlativo, d.proveedor_responsable, d.nro_invoice, d.nro_bl, d.nro_booking].some((v) => String(v || '').toLowerCase().includes(search))) return false;
    return true;
  });
  const filteredRequests = selectedProviderId ? data.solicitudes.filter((s) => String(s.id_proveedor) === String(selectedProviderId)) : data.solicitudes;

  const setSelectedProvider = (providerId) => {
    setFilters((prev) => ({ ...prev, proveedor: String(providerId) }));
    setTab('cuenta');
  };
  const changeSelectedState = async (estado) => {
    if (!selectedNfs.length) return notificar.aviso('Seleccione al menos una NF.');
    if (estado === 'Pagado' && !window.confirm(`¿Confirma el pago efectivo de ${selectedNfs.length} NF?`)) return;
    try {
      const response = await fetch(`${urlApiProd}/finanzas/proveedores/notas/estado`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ estado, nf_ids: selectedNfs.map((nf) => nf.id) }) });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'No fue posible actualizar las NF.');
      notificar.exito(`${selectedNfs.length} NF actualizada(s) a ${estado}.`); setSelected(new Set()); loadData();
      window.dispatchEvent(new Event('finanzas-alertas-updated'));
    } catch (error) { notificar.error(error.message); }
  };
  const generated = async (solicitud) => {
    const providerRequests = [...filteredRequests, { ...solicitud, fecha_generacion: new Date().toISOString(), estado: 'Generada' }];
    generarCorreoOutlookConExcel({ solicitud, solicitudes: providerRequests, descuentos: filteredDiscounts });
    notificar.exito(`${solicitud.correlativo} generada. Abra el .eml descargado: Outlook lo abre como borrador.`);
    window.dispatchEvent(new Event('finanzas-alertas-updated'));
    setSelected(new Set()); await loadData();
  };
  const exportCurrent = () => exportControlExcel({ notas: accountRows, solicitudes: filteredRequests, descuentos: filteredDiscounts }, `Control_pago_${selectedNfs[0]?.proveedor || 'proveedores'}.xlsx`);
  const openNewDiscount = () => { setEditingDiscount(null); setDiscountModal(true); };
  const openEditDiscount = (item) => { setEditingDiscount(item); setDiscountModal(true); };
  const closeDiscountModal = () => { setDiscountModal(false); setEditingDiscount(null); };

  return (
    <main className="container-fluid ui-page fin-page">
      <PageHeader
        eyebrow="Finanzas"
        title="Control de Pago a Proveedores"
        descripcion="Estado de cuenta de proveedores, saldos por nota fiscal y descuentos."
        actions={
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={exportCurrent}><LuDownload /> Exportar Excel</button>
            <button type="button" className="ui-btn ui-btn-primary" onClick={openNewDiscount}><LuPlus /> Nuevo descuento</button>
          </>
        }
      />
      <section className="fin-section"><div className="fin-section-title fin-global-title"><div><LuCircleDollarSign /><span><small>Todas las Notas Fiscales</small><strong>Resumen global</strong></span></div><div className="fin-global-range"><label><span>Desde</span><DateInput plain value={globalRange.desde} max={globalRange.hasta || undefined} onChange={(e) => setGlobalRange((prev) => ({ ...prev, desde: e.target.value }))} /></label><label><span>Hasta</span><DateInput plain value={globalRange.hasta} min={globalRange.desde || undefined} onChange={(e) => setGlobalRange((prev) => ({ ...prev, hasta: e.target.value }))} /></label><RefreshIconButton title="Restablecer al año en curso" onClick={() => setGlobalRange(currentYearRange())} /></div></div><SummaryGrid data={data.resumen_global} /></section>
      <section className="fin-section"><div className="fin-section-title"><div><LuSearch /><span><small>Saldos acumulados</small><strong>Resumen por proveedor</strong></span></div></div><Filters filters={filters} setFilters={setFilters} activeExtras={activeExtras} setActiveExtras={setActiveExtras} proveedores={data.proveedores} estados={data.estados_pago} /><ProviderSummaryTable rows={data.resumen_proveedores} onSelect={setSelectedProvider} /></section>
      <section className="fin-section fin-account-section">
        <div className="fin-tabs" role="tablist"><button className={tab === 'cuenta' ? 'active' : ''} onClick={() => setTab('cuenta')}>Estado de cuenta</button><button className={tab === 'descuentos' ? 'active' : ''} onClick={() => setTab('descuentos')}>Extracostos / Descuentos <span>{filteredDiscounts.length}</span></button><button className={tab === 'solicitudes' ? 'active' : ''} onClick={() => setTab('solicitudes')}>Solicitudes <span>{filteredRequests.length}</span></button></div>
        {tab === 'cuenta' && (!selectedProviderId ? (
          <div className="fin-provider-empty">
            <LuSearch />
            <strong>Seleccione un proveedor para ver su estado de cuenta.</strong>
          </div>
        ) : (
          <>
            <div className="fin-provider-heading"><div><small>Estado de cuenta por proveedor</small><h2>{data.proveedores.find((p) => String(p.id_proveedor) === String(selectedProviderId))?.nombre}</h2></div><div className="fin-provider-kpis"><span>Total comprado <strong>{fmtM3(providerSummary.total_m3_comprado)} M3</strong></span><span>Pendiente <strong>{fmtM3(providerSummary.total_m3_pendiente)} M3</strong></span><span>Pendiente <strong>{fmtBRL(providerSummary.total_brl_pendiente)}</strong></span></div></div>
            <div className="fin-actionbar"><span>{selected.size ? `${selected.size} NF seleccionada(s)` : `${accountRows.length} NF`}</span><div><button className="fin-button secondary" disabled={!selected.size} onClick={() => changeSelectedState('En revisión')}>Enviar a revisión</button><button className="fin-button secondary" disabled={!selected.size} onClick={() => changeSelectedState('Pagado')}><LuCheck /> Marcar pagado</button><button className="fin-button primary" title={!canGeneratePaymentRequest && selected.size ? 'La selección contiene NF con una solicitud de pago ya generada.' : undefined} disabled={!canGeneratePaymentRequest} onClick={() => setPaymentModal(true)}><LuMailPlus /> Generar solicitud de pago</button></div></div>
            <AccountTable rows={pagedRows} selected={selected} setSelected={setSelected} selectedSupplierId={selectedNfs[0]?.id_proveedor} onProviderNeeded={() => notificar.aviso('Seleccione primero un proveedor; una solicitud no puede mezclar proveedores.')} />
            <div className="fin-pagination"><span>{accountRows.length ? `${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, accountRows.length)} de ${accountRows.length}` : '0 registros'}</span><button title="Página anterior" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><LuChevronLeft /></button><strong>{page} / {totalPages}</strong><button title="Página siguiente" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}><LuChevronRight /></button></div>
          </>
        ))}
        {tab === 'descuentos' && <><div className="fin-discount-filters"><input value={discountFilters.search} onChange={(e) => setDiscountFilters((p) => ({ ...p, search: e.target.value }))} placeholder="Buscar correlativo, Invoice, BL o Booking" /><select value={discountFilters.tipo} onChange={(e) => setDiscountFilters((p) => ({ ...p, tipo: e.target.value }))}><option value="">Todos los tipos</option>{DISCOUNT_TYPES.map((x) => <option key={x}>{x}</option>)}</select><select value={discountFilters.estado} onChange={(e) => setDiscountFilters((p) => ({ ...p, estado: e.target.value }))}><option value="">Todos los estados</option><option>Borrador</option><option>Pendiente de Descontar</option><option>Descontado</option></select><DateInput plain value={discountFilters.fecha} onChange={(e) => setDiscountFilters((p) => ({ ...p, fecha: e.target.value }))} /></div><div className="fin-table-wrap"><table className="fin-table"><thead><tr><th>Correlativo</th><th>Proveedor</th><th>Tipo</th><th>Fecha origen</th><th>Proveedor extracosto</th><th>Invoice</th><th>BL</th><th>Booking</th><th>Valor</th><th>Estado</th><th>Fecha aplicación</th></tr></thead><tbody>{filteredDiscounts.map((d) => <tr key={d.id}><td>{isDiscountEditable(d) ? <button type="button" className="fin-discount-correlativo-link" title="Editar descuento" onClick={() => openEditDiscount(d)}>{d.correlativo}</button> : <strong>{d.correlativo}</strong>}</td><td>{d.proveedor_responsable}</td><td>{d.tipo || '-'}</td><td>{fmtDate(d.fecha_origen)}</td><td>{d.proveedor_extracosto || '-'}</td><td>{d.nro_invoice || '-'}</td><td>{d.nro_bl || '-'}</td><td>{d.nro_booking || '-'}</td><td>{d.moneda} {num(d.valor).toLocaleString('es-CL')}</td><td><span className={`fin-status ${d.es_borrador ? 'review' : d.estado === 'Descontado' ? 'paid' : 'pending'}`}>{d.es_borrador ? 'Borrador' : d.estado}</span></td><td>{fmtDate(d.fecha_aplicacion)}</td></tr>)}{!filteredDiscounts.length && <tr><td colSpan="11" className="fin-empty">No hay descuentos registrados.</td></tr>}</tbody></table></div></>}
        {tab === 'solicitudes' && <div className="fin-table-wrap"><table className="fin-table"><thead><tr><th>Solicitud</th><th>Fecha</th><th>Proveedor</th><th>NF</th><th>M3</th><th>Total NF</th><th>Descuentos</th><th>Neto solicitado</th><th>Usuario</th><th>Estado</th></tr></thead><tbody>{filteredRequests.map((s) => <tr key={s.id}><td><strong>{s.correlativo}</strong></td><td>{fmtDate(s.fecha_generacion)}</td><td>{s.proveedor}</td><td>{s.nf_ids?.length || 0}</td><td>{fmtM3(s.total_m3)}</td><td>{fmtBRL(s.monto_bruto_brl)}</td><td>{fmtBRL(s.descuentos_brl)}</td><td>{fmtBRL(s.monto_neto_brl)}</td><td>{s.usuario}</td><td><span className="fin-status requested">{s.estado}</span></td></tr>)}{!filteredRequests.length && <tr><td colSpan="10" className="fin-empty">No hay solicitudes generadas.</td></tr>}</tbody></table></div>}
      </section>
      <DiscountModal show={discountModal} onHide={closeDiscountModal} proveedores={data.proveedores} notas={data.notas_fiscales} descuentos={data.descuentos} discount={editingDiscount} onSaved={loadData} />
      <PaymentModal show={paymentModal} onHide={() => setPaymentModal(false)} selectedNfs={selectedNfs} discounts={data.descuentos} onGenerated={generated} />
    </main>
  );
}
